import { deviceDetect, isMobile, isTablet, isDesktop } from 'react-device-detect'

import { HttpClient } from 'common/HttpClient'
import { Environment } from 'enviroment/AppSettings'
import { AnalyticsActionTypes, IAnalyticsActionDataToSend, IAnalyticsBrowserDataToSend, IAnalyticsEventDataToSend, IAnalyticsEventsTypesNamesResponse } from './AnalyticsServiceModels'
import { SessionStorage } from 'common/CookieStorage'
import { STORAGEKEYS } from 'enviroment/Keys'



//#region SettingService Instance
let instance: AnalyticsService
//#endregion


export class AnalyticsService {
   private readonly _client: HttpClient;
   private readonly _sessionStorage: SessionStorage;
   private readonly _eventTypesNamesUrlPath = "/internalAnalytics/eventTypesNames"
   private readonly _createSingleAnalyticEvent = "/internalAnalytics/createSingleAnalyticEvent";
   private readonly _browserDataAnalyticsUrlPath = "/internalAnalytics/browserData"
   private readonly _actionAnalyticsUrlPath = "/internalAnalytics/actions"
   private readonly _eventAnalyticsUrlPath = "/internalAnalytics"

   constructor() {
      if (instance) { //SINGLETON DESIGN PATTERN
         throw new Error("New instance cannot be created!!")
      }

      instance = this;
      this._client = new HttpClient(Environment.apiUrl)
      this._sessionStorage = new SessionStorage()
   }

   /**
    * Funkcja pomocnicza sprawdzająca czy w sessionStorage istnieje tablica z zapisanymi nazwami eventów które zostały już wysłane do Backendu.
    * !! LOGIKA BIZNESOWA: Dany event odkładamy w bazie danych tylko raz dla danego użytkownika !!
    * 
    *    1. Jeśli tablica nie istnieje jeszcze, oznacza to pierwszy wysyłany event - zapisujemy w sessionStorage nową tablicę na podstawie obecnie przekazanej nazwy eventu oraz pozwalamy na wysyłkę requesta do backendu
    *    2. Jeśli tablica istnieje, ale nie ma w niej obecnie przekazanej nazwy eventu - dodajemy tą nazwę do wcześniej utworzonej tablicy w sessionStorage oraz pozwalamy na wysyłkę requesta do backendu
    *    3. Jeśli tablica istnieje I POSIADA JUŻ OBECNIE PRZEKAZANĄ NAZWĘ EVENTU - wychodzimy z funkcji i nie pozwalamy na ponowną wysyłkę requesta do backendu
    * @returns { Boolean }
    */
   private _checkIfAnalyticsEventHasAlreadyBeenSend(eventTypeName: string): boolean {
      const sendedAnalyticsEventsArray = JSON.parse(this._sessionStorage.get(STORAGEKEYS.ANALYTICS_SERVICE_SENDED_EVENTS_NAMES)) as string[]

      if(sendedAnalyticsEventsArray === null) { // Jeśli nie ma tablicy z wysłanymi do Backendu eventami, tworzymy odpowiedni wpis w sessionStorage wrzucając obecną nazwę eventu. Następnie wykonujemy zapytanie do Backendu
         this._sessionStorage.add(STORAGEKEYS.ANALYTICS_SERVICE_SENDED_EVENTS_NAMES, JSON.stringify([eventTypeName]))
         
         return false
      }
      else { // Jeśli tablica z wysłanymi do Backendu eventami istnieje
         if(sendedAnalyticsEventsArray.includes(eventTypeName)) {
            return true//Kończymy działanie funkcji - event o takiej nazwie został już odłożony w bazie danych.
         }

         // Jeśli nie znaleziono w tablicy danego eventu, oznacza to że nie został on jeszcze wysłany. Kontynuujemy działanie funkcji, dodajemy event do tablicy w sessionStorage oraz wysyłamy request do Backendu
         sendedAnalyticsEventsArray.push(eventTypeName)
         this._sessionStorage.changeItemValue(STORAGEKEYS.ANALYTICS_SERVICE_SENDED_EVENTS_NAMES, JSON.stringify(sendedAnalyticsEventsArray))

         return false
      }
   }

   public async sendBrowserData() {
      const deviceDetectionObject =  deviceDetect(window.navigator.userAgent)

      const analyticsAdditionalDataToSend: IAnalyticsBrowserDataToSend = {
         deviceType: ((isMobile && 'mobile') || (isDesktop && 'desktop') || (isTablet && 'tablet') || "unknown"),
         deviceSystem: deviceDetectionObject.osName,
         deviceSystemDetails: deviceDetectionObject.osVersion,
         deviceBrowser: deviceDetectionObject.browserName,
         deviceBrowserEngine: deviceDetectionObject.engineName,
         deviceBrowserDetails: deviceDetectionObject.browserFullVersion,
         screenResolution: `${window.screen.width}x${window.screen.height}`
      }

      await this._client.post<{}, IAnalyticsBrowserDataToSend>(this._browserDataAnalyticsUrlPath, analyticsAdditionalDataToSend, true)
   }

   public async sendAnalyticEvent(eventTypeName: string) {
      const analyticsEventDataToSend: IAnalyticsEventDataToSend = { eventType: eventTypeName }
      !this._checkIfAnalyticsEventHasAlreadyBeenSend(eventTypeName) &&  await this._client.post<{}, IAnalyticsEventDataToSend>(this._eventAnalyticsUrlPath, analyticsEventDataToSend, true)
   }

   public async sendAnalyticAction(actionName: string, actionId: AnalyticsActionTypes) {
      const analyticsActionDataToSend: IAnalyticsActionDataToSend = {
         name: actionName,
         actionId: actionId,
      }

      await this._client.post<{}, IAnalyticsActionDataToSend>(this._actionAnalyticsUrlPath, analyticsActionDataToSend, true)
   }

   public async getAnalyticsEventTypes(): Promise<IAnalyticsEventsTypesNamesResponse>{
      return (await this._client.get<IAnalyticsEventsTypesNamesResponse>(this._eventTypesNamesUrlPath, true)).data
   }


   /**
    * Funkcja tworząca pojedyncze zdarzenie analityczne za każdym razem gdy zostanie wywołana. Zdarzenia te są za każdym razem tworzone na nowo i nie są aktualizowane (założenie biznesowe)
    * @param eventTypeName 
    */
   public async createSingleAnalyticEvent(eventTypeName?: string | null, additionallyData?: string): Promise<void> {
     if(!eventTypeName || eventTypeName.length === 0){
       console.warn("Nie udało się zapisać eventu analitycznego - brak nazwy eventu")
       return
     }
     else {
       const analyticsEventDataToSend: IAnalyticsEventDataToSend = { eventType: eventTypeName }
 
       if(additionallyData?.length){
         analyticsEventDataToSend.additionallyData = additionallyData
       }
 
       await this._client.post<{}, IAnalyticsEventDataToSend>(this._createSingleAnalyticEvent, analyticsEventDataToSend, true)
     }
   }
}

let analyticsService = Object.freeze(new AnalyticsService())//SINGLETON DESIGN PATTERN
export default analyticsService;